import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import useFetchData from '../../../../hooks/useFetchData'
import { getStudentPeriodicalReportChart } from '../../../../services/periodical-report/periodical-report-marking.service'
import { getUsersByRoleIdSkipLimit } from '../../../../services/user/user.service'
import Loading from '../../../Loading/Loading'
import CustomAvatar from '../../../avatar/Avatar'

// MF-496 — Fixed BI "Periodical Report" chart. Replicates the Tableau Semester
// Report: per-student achievement levels across every reporting period, grouped
// by subject and strand, coloured by a 5-point band relative to the expected
// (year-level-aligned) level, with a grey "expected" step behind each bar.

const EXPECTED_COLOR = 'rgba(120, 132, 145, 0.32)'
const AXIS_INK = '#8a94a2'
const GRID_LINE = '#eef1f4'

const roleIdsOf = (user) => (user?.roles || []).map((role) => role.id)

const studentIdOf = (student) => student?._id || student?.studentId || student?.userId || null

// "2024.1" -> "Sem 1\n2024"; falls back to the raw code / name.
const periodLabel = (period) => {
  if (!period) return ''
  const match = String(period.code || '').match(/^(\d{4})\.(\d+)$/)
  if (match) return `Sem ${match[2]}\n${match[1]}`
  return period.name || period.code || ''
}

// Curriculum "kind" of a subject, derived from its areas' type — the optional
// top grouping level (no hardcoded per-learning-area rules).
const KIND_LABEL = (type) =>
  type === 'dimension' ? 'Strands & Modes' : type === 'domain' ? 'Disciplines' : 'Other'
const subjectType = (subject) => subject?.strands?.[0]?.type || ''

const buildOption = (chart, { groupByKind = false } = {}) => {
  const { periods = [], scale = {}, bands = [] } = chart
  const bandColor = Object.fromEntries(bands.map((band) => [band.key, band.color]))
  const cellAt = (strand, periodCode) => (strand.cells || []).find((cell) => cell.periodCode === periodCode)

  // When grouping by kind, order subjects so same-kind blocks are contiguous
  // (Strands & Modes first, then Disciplines).
  const subjects = groupByKind
    ? [...(chart.subjects || [])].sort(
        (a, b) => (subjectType(a) === 'dimension' ? 0 : 1) - (subjectType(b) === 'dimension' ? 0 : 1)
      )
    : chart.subjects || []

  // Leaves: one bar per (subject, strand, period) so the period sits on the
  // axis under each bar and periods align across strands (Tableau layout).
  const leaves = []
  const subjectRanges = []
  const strandRanges = []
  subjects.forEach((subject, subjectIndex) => {
    const subjectStart = leaves.length
    subject.strands.forEach((strand) => {
      const strandStart = leaves.length
      periods.forEach((period) => {
        leaves.push({
          subject: subject.name,
          strand: strand.name,
          period,
          cell: cellAt(strand, period.code)
        })
      })
      strandRanges.push({
        name: strand.name,
        start: strandStart,
        end: leaves.length - 1,
        subjectIndex
      })
    })
    subjectRanges.push({
      name: subject.name,
      start: subjectStart,
      end: leaves.length - 1
    })
  })

  // Unique category ids keep markArea/markLine unambiguous; labels come from
  // formatters that read back into `leaves`.
  const categories = leaves.map((_, index) => String(index))
  const midLabels = (ranges) => {
    const labels = new Array(leaves.length).fill('')
    ranges.forEach((range) => {
      labels[Math.floor((range.start + range.end) / 2)] = range.name
    })
    return labels
  }
  const strandLabels = midLabels(strandRanges)
  const subjectLabels = midLabels(subjectRanges)

  // Optional 3rd level: contiguous spans of subjects sharing a kind.
  const kindRanges = []
  if (groupByKind) {
    subjectRanges.forEach((range, index) => {
      const kind = KIND_LABEL(subjectType(subjects[index]))
      const last = kindRanges[kindRanges.length - 1]
      if (last && last.name === kind) last.end = range.end
      else kindRanges.push({ name: kind, start: range.start, end: range.end })
    })
  }
  const kindLabels = midLabels(kindRanges)

  const expectedData = leaves.map((leaf, index) => [
    index,
    leaf.cell && leaf.cell.expected != null ? leaf.cell.expected : null
  ])
  const achievedData = leaves.map((leaf) => {
    const cell = leaf.cell
    if (!cell || cell.level == null) return '-'
    return {
      value: cell.level,
      itemStyle: { color: bandColor[cell.band] || '#9aa5b1', borderRadius: [3, 3, 0, 0] },
      meta: { ...cell, strand: leaf.strand, period: leaf.period.name || leaf.period.code }
    }
  })

  // Box each strand group (Tableau-style division between groups), with a faint
  // fill that alternates per subject so subjects still read as blocks.
  const strandBoxes = {
    silent: true,
    z: 0,
    data: strandRanges.map((range) => [
      {
        xAxis: String(range.start),
        itemStyle: {
          color: range.subjectIndex % 2 === 0 ? 'rgba(46,87,132,0.03)' : 'rgba(46,87,132,0.07)',
          borderColor: '#dbe1e8',
          borderWidth: 1
        }
      },
      { xAxis: String(range.end) }
    ])
  }
  // Dividers between subjects, plus a heavier one between kinds (3-level view).
  const separatorData = subjectRanges.slice(1).map((range) => ({
    xAxis: String(range.start),
    lineStyle: { color: '#cbd2db', width: 1, type: 'dashed' }
  }))
  if (groupByKind) {
    kindRanges.slice(1).forEach((range) => {
      separatorData.push({
        xAxis: String(range.start),
        lineStyle: { color: '#8a94a2', width: 1.5, type: 'solid' }
      })
    })
  }
  const subjectSeparators = {
    silent: true,
    symbol: 'none',
    label: { show: false },
    data: separatorData
  }

  const yMax = scale.max ? Math.ceil(scale.max) : undefined

  const groupAxis = (data, offset, fontSize, fontWeight, color, width) => ({
    type: 'category',
    position: 'top',
    offset,
    data: categories,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      interval: 0,
      fontSize,
      fontWeight,
      color,
      width,
      overflow: 'break',
      lineHeight: fontSize + 3,
      formatter: (value) => data[Number(value)] || ''
    }
  })

  return {
    backgroundColor: 'transparent',
    grid: { left: 46, right: 56, top: groupByKind ? 130 : 98, bottom: 58 },
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (point) => {
        const meta = point?.data?.meta
        if (!meta) return ''
        const band = bands.find((item) => item.key === meta.band)
        const expected = meta.expected != null ? Number(meta.expected).toFixed(1) : '—'
        return `
          <b>${meta.strand}</b><br/>
          ${meta.period}<br/>
          Achieved: <b>${Number(meta.level).toFixed(1)}</b><br/>
          Expected: ${expected}<br/>
          ${band ? `<span style="color:${band.color}">■</span> ${band.label}` : ''}
        `
      }
    },
    xAxis: [
      {
        type: 'category',
        data: categories,
        axisTick: { alignWithLabel: true },
        axisLine: { lineStyle: { color: '#d6dbe2' } },
        axisLabel: {
          interval: 0,
          fontSize: 9,
          lineHeight: 12,
          color: AXIS_INK,
          formatter: (value) => periodLabel(leaves[Number(value)]?.period)
        }
      },
      groupAxis(strandLabels, 2, 10, 600, '#5a6472', 130),
      groupAxis(subjectLabels, groupByKind ? 42 : 44, 12, 700, '#3b4655', 140),
      ...(groupByKind ? [groupAxis(kindLabels, 82, 13, 800, '#25344a', 170)] : [])
    ],
    yAxis: {
      type: 'value',
      min: 0,
      max: yMax,
      name: 'Level',
      nameTextStyle: { fontSize: 10, color: AXIS_INK, align: 'right' },
      axisLabel: { fontSize: 10, color: AXIS_INK },
      splitLine: { lineStyle: { color: GRID_LINE } }
    },
    series: [
      {
        // Expected level — grey backdrop centred behind each bar (custom rect
        // so it stays centred regardless of the achieved bar width).
        name: 'Expected',
        type: 'custom',
        z: 1,
        silent: true,
        renderItem: (params, api) => {
          const index = api.value(0)
          const value = api.value(1)
          if (value == null || Number.isNaN(value)) return null
          const base = api.coord([index, 0])
          const top = api.coord([index, value])
          const width = api.size([1, 0])[0] * 0.82
          return {
            type: 'rect',
            shape: {
              x: base[0] - width / 2,
              y: top[1],
              width,
              height: base[1] - top[1]
            },
            style: { fill: EXPECTED_COLOR }
          }
        },
        data: expectedData
      },
      {
        // Achieved level — coloured per point by its band.
        name: 'Achieved',
        type: 'bar',
        z: 2,
        barWidth: '52%',
        label: {
          show: true,
          position: 'top',
          fontSize: 9,
          color: '#4a5563',
          formatter: (item) => (typeof item.value === 'number' ? item.value.toFixed(1) : '')
        },
        labelLayout: { hideOverlap: false },
        data: achievedData,
        markArea: strandBoxes,
        markLine: subjectSeparators
      }
    ]
  }
}

const BandLegend = ({ bands }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 14,
      justifyContent: 'center',
      padding: '4px 0 12px'
    }}
  >
    {bands.map((band) => (
      <div key={band.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            width: 13,
            height: 13,
            borderRadius: 3,
            background: band.color,
            display: 'inline-block'
          }}
        />
        <span style={{ fontSize: 11, color: '#5a6472' }}>{band.label}</span>
      </div>
    ))}
  </div>
)

const HeaderCard = ({ student }) => {
  if (!student) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: 14,
        border: '1px solid #e6eaef',
        borderRadius: 10,
        background: '#fff',
        minWidth: 280
      }}
    >
      <CustomAvatar
        size='large'
        variant='rounded'
        alt={student.code || student.fullName}
        src={student.photo ? `photos/${student.photo}` : null}
      />
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#2b3644' }}>
          {student.fullName}
          {student.code ? ` (${student.code})` : ''}
        </div>
        {student.yearLevel ? (
          <div style={{ fontSize: 12, color: '#6a7482' }}>
            Year level: <b>{student.yearLevel}</b>
          </div>
        ) : null}
        {student.formGroup ? (
          <div style={{ fontSize: 12, color: '#6a7482' }}>
            Form group: <b>{student.formGroup}</b>
          </div>
        ) : null}
        {student.house ? (
          <div style={{ fontSize: 12, color: '#6a7482' }}>
            House: <b>{student.house}</b>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const PeriodicalReportChart = () => {
  const { isLoading: userLoading, data: user } = useFetchData('auth/profile')
  const [selected, setSelected] = useState(null)
  const [students, setStudents] = useState([])
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [studentsLoaded, setStudentsLoaded] = useState(false)
  const [chart, setChart] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hiddenSubjects, setHiddenSubjects] = useState(() => new Set())
  const [groupBy, setGroupBy] = useState('learning-area')

  const roles = roleIdsOf(user)
  const isStudent = roles.includes('Student')
  const isParent = roles.includes('Parent')
  const isStaff = roles.some((role) => !['Student', 'Parent'].includes(role))
  const canPickAny = isStaff
  const parentChildren = useMemo(() => (isParent ? user?.children || [] : []), [isParent, user])

  // Initial scoping: a Student sees only themselves; a Parent is limited to
  // their children (auto-selecting when there is a single child).
  useEffect(() => {
    if (!user || selected) return
    if (isStaff) return
    if (isStudent) {
      setSelected({
        _id: user._id || user.userId,
        fullName: user.fullName || user.value,
        code: user.code
      })
    } else if (isParent && parentChildren.length) {
      setSelected(parentChildren[0])
    }
  }, [user, isStaff, isStudent, isParent, parentChildren, selected])

  // Load the staff roster lazily — only when the selector is first opened — so
  // entering the report stays fast instead of paying the fetch up front.
  const loadStudents = () => {
    if (!isStaff || studentsLoaded || studentsLoading) return
    setStudentsLoaded(true)
    setStudentsLoading(true)
    getUsersByRoleIdSkipLimit('Student', 0, 500, {})
      .then((res) => setStudents(res?.data?.users || []))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false))
  }

  // Load the chart whenever the selected student changes.
  useEffect(() => {
    const id = studentIdOf(selected)
    if (!id) return
    setLoading(true)
    setError(null)
    getStudentPeriodicalReportChart(id)
      .then((res) => {
        setChart(res?.data || null)
        setHiddenSubjects(new Set())
      })
      .catch(() => {
        setChart(null)
        setError('This student has no periodical report data yet.')
      })
      .finally(() => setLoading(false))
  }, [selected])

  // Learning-area (subject) filter — hide/show subjects client-side.
  const visibleChart = useMemo(() => {
    if (!chart) return null
    return {
      ...chart,
      subjects: (chart.subjects || []).filter((subject) => !hiddenSubjects.has(subject.key))
    }
  }, [chart, hiddenSubjects])

  const option = useMemo(
    () =>
      visibleChart && visibleChart.subjects.length
        ? buildOption(visibleChart, { groupByKind: groupBy === 'kind' })
        : null,
    [visibleChart, groupBy]
  )

  const selectorOptions = canPickAny ? students : parentChildren
  const showSelector = canPickAny || parentChildren.length > 1

  const onSelectStudent = (id) => {
    const found = selectorOptions.find((item) => String(studentIdOf(item)) === id)
    setSelected(found || null)
  }

  const toggleSubject = (key) => {
    setHiddenSubjects((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Minimum content width so each bar has room; the chart fills the screen when
  // it fits and scrolls horizontally (like Tableau) when it is wider.
  const contentWidth = useMemo(() => {
    if (!visibleChart) return undefined
    const strandCount = (visibleChart.subjects || []).reduce(
      (acc, subject) => acc + (subject.strands?.length || 0),
      0
    )
    const leafCount = strandCount * Math.max(visibleChart.periods?.length || 1, 1)
    return leafCount * 76 + 120
  }, [visibleChart])

  return (
    // width/minWidth:0 + overflow-x:hidden keeps this pane bounded to the space
    // left by the sidebar (avoids the flexbox min-width:auto blow-out); only the
    // chart below scrolls horizontally, and the centred title stays centred.
    // height:100% + overflow-y:auto lets the pane scroll vertically inside the
    // fixed-height module body, so on shorter screens the bottom of the chart
    // (period labels / legend) stays reachable instead of being clipped.
    <div
      style={{
        padding: '8px 4px',
        width: '100%',
        height: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'auto'
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          marginBottom: 12
        }}
      >
        <HeaderCard student={chart?.student} />
        {showSelector ? (
          <div style={{ width: 320, maxWidth: '100%' }}>
            <label
              style={{
                display: 'block',
                fontSize: 11,
                color: '#8a94a2',
                marginBottom: 4
              }}
            >
              Student Name
            </label>
            <select
              value={studentIdOf(selected) || ''}
              onFocus={loadStudents}
              onMouseDown={loadStudents}
              onChange={(event) => onSelectStudent(event.target.value)}
              style={{
                width: '100%',
                padding: '9px 11px',
                borderRadius: 8,
                border: '1px solid #d5dbe4',
                fontSize: 13,
                color: '#3b4655',
                background: '#fff'
              }}
            >
              <option value=''>{studentsLoading ? 'Loading students…' : 'Select a student…'}</option>
              {selectorOptions.map((item) => {
                const id = String(studentIdOf(item))
                const name =
                  item.fullName || [item.lastName, item.firstName].filter(Boolean).join(', ') || item.value
                return (
                  <option key={id} value={id}>
                    {name}
                    {item.code ? ` (${item.code})` : ''}
                  </option>
                )
              })}
            </select>
          </div>
        ) : null}
      </div>

      <div style={{ textAlign: 'center', marginBottom: 4 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 2,
            color: '#2b3644'
          }}
        >
          PERIODICAL REPORT
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#4a5563' }}>
          Victorian Curriculum F-10 achievement standards
        </div>
        <div style={{ fontSize: 11, color: '#8a94a2' }}>Age-related 5-point standard scale</div>
      </div>

      {chart?.bands ? <BandLegend bands={chart.bands} /> : null}

      {chart ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '0 0 10px'
          }}
        >
          <span style={{ fontSize: 11, color: '#8a94a2' }}>Group by:</span>
          {[
            { key: 'learning-area', label: 'Learning Area' },
            { key: 'kind', label: 'Kind ▸ Learning Area' }
          ].map((opt) => {
            const on = groupBy === opt.key
            return (
              <button
                key={opt.key}
                onClick={() => setGroupBy(opt.key)}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 11px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  border: '1px solid ' + (on ? '#2b5784' : '#d5dbe4'),
                  background: on ? '#2b5784' : '#fff',
                  color: on ? '#fff' : '#5a6472'
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      ) : null}

      {chart?.subjects?.length > 1 ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'center',
            padding: '0 0 12px'
          }}
        >
          {chart.subjects.map((subject) => {
            const on = !hiddenSubjects.has(subject.key)
            return (
              <button
                key={subject.key}
                onClick={() => toggleSubject(subject.key)}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 11px',
                  borderRadius: 14,
                  cursor: 'pointer',
                  border: '1px solid ' + (on ? '#2b5784' : '#d5dbe4'),
                  background: on ? '#2b5784' : '#fff',
                  color: on ? '#fff' : '#98a2b0'
                }}
              >
                {subject.name}
              </button>
            )
          })}
        </div>
      ) : null}

      {loading || userLoading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#98a2b0', textAlign: 'center' }}>{error}</div>
      ) : !option ? (
        <div style={{ padding: 24, color: '#98a2b0', textAlign: 'center' }}>
          {chart ? 'No learning areas selected.' : 'Select a student to view their periodical report.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
          <div style={{ minWidth: contentWidth }}>
            <ReactECharts option={option} style={{ height: 500 }} notMerge opts={{ renderer: 'svg' }} />
          </div>
        </div>
      )}
    </div>
  )
}

export default PeriodicalReportChart
